import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ClipboardCheck, Eye, Copy, Pencil, Trash2, UserPlus, Plus, Loader2, ArrowUp, ArrowDown, Send, Inbox, Settings2, Star, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI } from '@/lib/api';
import { MitgliedSuche, datum, fehlertext } from '@/pages/ErnaehrungPage';
import { KoerperanalyseSchalter } from '@/pages/ErnaehrungKoerperanalyse';

/* Formulare und Check-ins (10.10.2026, Paket 4): wöchentlicher Check-in automatisch, Studio- und eigene Formulare,
   Zuweisen, Antworten mit Feedback vom Coach. Der Server prüft Antworten je Fragetyp.
   E5 (15.10.2026): Feldtypen Bewertung/Foto, Smart-Felder (Umfänge, Fortschrittsfotos, Gewicht, Einschränkungen,
   Aktivität), eigene Metriken mit Richtung „besser“, Formular-Arten, Zeitplan mit Uhrzeit und Erinnerung, je Kunde
   eigenes Formular, Zeitplan und ausgeblendete Felder; Entwurf-Status und „Als gelesen markieren“ als Umschalter. */

const WOCHENTAG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const TYP = {
  skala: 'Skala 1–10', auswahl: 'Auswahl (eine)', mehrfach: 'Auswahl (mehrere)', ja_nein: 'Ja / Nein', zahl: 'Zahl', text: 'Freitext',
  gewicht: 'Gewicht (kg → Verlauf)', bewertung: 'Bewertung 1–5 (Smiley/Sterne)', foto: 'Foto', umfaenge: 'Umfänge (Taille, Hüfte, Oberschenkel, Brust)',
  fortschrittsfotos: 'Fortschrittsfotos (vorne, seitlich, hinten)', ernaehrungseinschraenkungen: 'Ernährungseinschränkungen', aktivitaetslevel: 'Aktivitätslevel',
};
const SMART = {
  gewicht: { text: 'Dein Gewicht heute Morgen (nüchtern)', label: 'Gewicht' },
  umfaenge: { text: 'Deine Umfänge in cm', label: 'Umfänge' },
  fortschrittsfotos: { text: 'Deine Fortschrittsfotos (vorne, seitlich, hinten)', label: 'Fortschrittsfotos' },
  ernaehrungseinschraenkungen: { text: 'Was lässt du bei der Ernährung weg?', label: 'Einschränkungen' },
  aktivitaetslevel: { text: 'Wie aktiv bist du im Alltag?', label: 'Aktivitätslevel' },
};
const ARTEN = { onboarding: 'Onboarding', wochencheckin: 'Wöchentlicher Check-in', frei: 'Nach Bedarf' };
const METRIK_TYP = { zahl: 'Zahl', text: 'Text', bild: 'Bild', bewertung: 'Bewertung 1–5' };
const BETTER = [{ value: '1', label: 'höher ist besser' }, { value: '-1', label: 'niedriger ist besser' }];
const STELLE = { taille: 'Taille', huefte: 'Hüfte', oberschenkel: 'Oberschenkel', brust: 'Brust' };
const AKTIVITAET = { kaum: 'kaum aktiv', wenig: 'wenig aktiv', mittel: 'mittel aktiv', hoch: 'sehr aktiv' };
const STATUS = {
  offen: { label: 'offen', cls: 'bg-amber-500/10 text-amber-700 border-amber-200' },
  entwurf: { label: 'Entwurf', cls: 'bg-sky-500/10 text-sky-700 border-sky-200' },
  verpasst: { label: 'überfällig', cls: 'bg-red-500/10 text-red-700 border-red-200' },
  beantwortet: { label: 'beantwortet', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-200' },
};
const lang = { day: '2-digit', month: '2-digit', year: 'numeric' };
const mitBewertung = (typ) => ['skala', 'bewertung', 'zahl', 'gewicht'].includes(typ);

function AntwortWert({ f, wert }) {
  if (wert === null || wert === undefined) return <span className="text-muted-foreground">–</span>;
  if (f.typ === 'skala') {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="h-1.5 w-24 rounded-full bg-muted overflow-hidden" aria-hidden="true"><span className="block h-full bg-primary" style={{ width: `${wert * 10}%` }} /></span>
        <span className="tabular-nums font-medium">{wert}/10</span>
      </span>
    );
  }
  if (f.typ === 'bewertung') {
    return (
      <span className="inline-flex items-center gap-1" aria-label={`${wert} von 5`}>
        {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= wert ? 'fill-primary text-primary' : 'text-muted-foreground/40'}`} aria-hidden="true" />)}
        <span className="tabular-nums font-medium ml-1">{wert}/5</span>
      </span>
    );
  }
  if (f.typ === 'ja_nein') return <span className="font-medium">{wert ? 'Ja' : 'Nein'}</span>;
  if (f.typ === 'umfaenge') return <span className="font-medium tabular-nums">{Object.entries(wert).map(([s, cm]) => `${STELLE[s] || s} ${String(cm).replace('.', ',')} cm`).join(' · ')}</span>;
  if (f.typ === 'fortschrittsfotos') return <span className="font-medium">{Object.keys(wert).length} Foto(s): {Object.keys(wert).join(', ')}</span>;
  if (f.typ === 'foto') return <span className="font-medium">1 Foto</span>;
  if (f.typ === 'aktivitaetslevel') return <span className="font-medium">{AKTIVITAET[wert] || wert}</span>;
  if (f.typ === 'ernaehrungseinschraenkungen') return <span className="font-medium">{wert.length ? wert.join(', ') : 'keine'}</span>;
  if (Array.isArray(wert)) return <span className="font-medium">{wert.join(', ')}</span>;
  if (f.typ === 'zahl' || f.typ === 'gewicht') return <span className="font-medium tabular-nums">{String(wert).replace('.', ',')} {f.einheit || ''}</span>;
  return <span className="whitespace-pre-line">{wert}</span>;
}

export function Antworten({ z }) {
  const antworten = z.antworten || z.entwurf || {};
  return (
    <dl className="space-y-2 text-sm">
      {z.fragen.map((f) => (
        <div key={f.id} className="grid sm:grid-cols-[1fr_auto] gap-x-3">
          <dt className="text-muted-foreground">{f.text}{f.metrik && <span className="ml-1 text-xs">(Metrik)</span>}</dt>
          <dd className="sm:text-right"><AntwortWert f={f} wert={antworten[f.id]} /></dd>
        </div>
      ))}
    </dl>
  );
}

function Vorschau({ f, onClose }) {
  return (
    <Dialog open={!!f} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f?.titel}</DialogTitle>{f?.beschreibung && <DialogDescription>{f.beschreibung}</DialogDescription>}</DialogHeader>
        {f && <p className="text-xs text-muted-foreground">{ARTEN[f.formular_art] || ARTEN.frei}</p>}
        <ol className="space-y-3 text-sm list-decimal pl-5">
          {f?.fragen.map((q) => (
            <li key={q.id}>
              <p className="font-medium">{q.text}{q.pflicht ? '' : <span className="font-normal text-muted-foreground"> (freiwillig)</span>}</p>
              <p className="text-xs text-muted-foreground">{TYP[q.typ]}{q.optionen && !['ernaehrungseinschraenkungen', 'aktivitaetslevel'].includes(q.typ) ? `: ${q.optionen.join(' · ')}` : ''}{q.typ === 'skala' && (q.links || q.rechts) ? `: 1 = ${q.links}, 10 = ${q.rechts}` : ''}{q.einheit ? ` (${q.einheit})` : ''}{mitBewertung(q.typ) && q.better === -1 ? ' · niedriger ist besser' : ''}</p>
            </li>
          ))}
          {(f?.metriken || []).map((m) => (
            <li key={m.id}>
              <p className="font-medium">{m.name} <span className="font-normal text-muted-foreground">(eigene Metrik, freiwillig)</span></p>
              <p className="text-xs text-muted-foreground">{METRIK_TYP[m.typ]}{m.einheit ? ` (${m.einheit})` : ''}{m.better === -1 ? ' · niedriger ist besser' : ''}</p>
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
}

function Editor({ start, daten, onClose, onGespeichert }) {
  const [f, setF] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const typen = daten?.typen || Object.keys(TYP);
  const typName = (t) => daten?.typ_namen?.[t] || TYP[t] || t;
  useEffect(() => {
    if (!start) { setF(null); return; }
    setF({
      id: start.art === 'eigen' ? start.id : null, titel: start.art === 'studio' ? `${start.titel} (angepasst)` : start.titel || '',
      kategorie: start.kategorie || 'individuell', formular_art: start.formular_art || 'frei', beschreibung: start.beschreibung || '',
      vorlage: start.art === 'studio' ? start.id : start.vorlage,
      fragen: (start.fragen || [{ typ: 'skala', text: '', pflicht: true, better: 1 }]).map((q) => ({ ...q, optionenText: (q.optionen || []).join('\n') })),
      metriken: (start.metriken || []).map((m) => ({ ...m })),
    });
  }, [start]);
  if (!f) return null;
  const frage = (i, neu) => setF({ ...f, fragen: f.fragen.map((q, j) => (j === i ? { ...q, ...neu } : q)) });
  const schieben = (i, d) => { const a = [...f.fragen]; [a[i], a[i + d]] = [a[i + d], a[i]]; setF({ ...f, fragen: a }); };
  const metrik = (i, neu) => setF({ ...f, metriken: f.metriken.map((m, j) => (j === i ? { ...m, ...neu } : m)) });
  const smartDa = (t) => f.fragen.some((q) => q.typ === t);
  const smartDazu = (t) => setF({ ...f, fragen: [...f.fragen, { typ: t, text: SMART[t].text, pflicht: t !== 'umfaenge' && t !== 'fortschrittsfotos', better: t === 'gewicht' ? -1 : 1 }] });
  const speichern = async (e) => {
    e.preventDefault();
    setLaeuft(true);
    const daten_ = {
      titel: f.titel.trim(), kategorie: f.kategorie, formular_art: f.formular_art, beschreibung: f.beschreibung, vorlage: f.vorlage || null,
      fragen: f.fragen.map(({ optionenText, ...q }) => ({
        ...q, better: Number(q.better) === -1 ? -1 : 1,
        optionen: ['auswahl', 'mehrfach'].includes(q.typ) ? optionenText.split('\n').map((o) => o.trim()).filter(Boolean) : undefined,
      })),
      metriken: f.metriken.map((m) => ({ id: m.id || null, name: m.name, typ: m.typ, einheit: m.einheit || null, better: Number(m.better) === -1 ? -1 : 1 })),
    };
    try {
      if (f.id) await ernaehrungAPI.formularAendern(f.id, daten_); else await ernaehrungAPI.formularNeu(daten_);
      toast.success('Formular gespeichert'); onGespeichert(); onClose();
    } catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? 'Formular bearbeiten' : 'Neues Formular'}</DialogTitle>
          <DialogDescription>Offene Zuweisungen behalten die Felder, mit denen sie verschickt wurden.</DialogDescription></DialogHeader>
        <form onSubmit={speichern} className="space-y-3">
          <div className="grid sm:grid-cols-[1fr_11rem_11rem] gap-2">
            <FormField label="Titel"><Input required minLength={3} maxLength={80} value={f.titel} onChange={(e) => setF({ ...f, titel: e.target.value })} /></FormField>
            <FormField label="Art"><NativeSelect value={f.formular_art} onChange={(v) => setF({ ...f, formular_art: v })} options={Object.entries(ARTEN).map(([value, label]) => ({ value, label }))} /></FormField>
            <FormField label="Kategorie"><NativeSelect value={f.kategorie} onChange={(v) => setF({ ...f, kategorie: v })} options={[['checkin', 'Check-in'], ['anamnese', 'Anamnese'], ['gewohnheiten', 'Gewohnheiten'], ['schlaf', 'Schlaf & Stress'], ['individuell', 'Individuell']].map(([value, label]) => ({ value, label }))} /></FormField>
          </div>
          <FormField label="Beschreibung (sieht das Mitglied)"><Input maxLength={300} value={f.beschreibung} onChange={(e) => setF({ ...f, beschreibung: e.target.value })} /></FormField>
          <ol className="space-y-2">
            {f.fragen.map((q, i) => (
              <li key={i} className="rounded-md border p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground w-6">{i + 1}.</span>
                  <NativeSelect aria-label={`Feldtyp ${i + 1}`} className="w-56" value={q.typ} onChange={(v) => frage(i, { typ: v })} options={typen.map((value) => ({ value, label: typName(value) }))} />
                  <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={q.pflicht !== false} onChange={(e) => frage(i, { pflicht: e.target.checked })} />Pflicht</label>
                  <div className="ml-auto flex">
                    <Button type="button" size="icon" variant="ghost" aria-label={`Feld ${i + 1} nach oben`} disabled={i === 0} onClick={() => schieben(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={`Feld ${i + 1} nach unten`} disabled={i === f.fragen.length - 1} onClick={() => schieben(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={`Feld ${i + 1} entfernen`} disabled={f.fragen.length === 1} onClick={() => setF({ ...f, fragen: f.fragen.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <Input aria-label={`Fragetext ${i + 1}`} required minLength={3} maxLength={300} value={q.text} onChange={(e) => frage(i, { text: e.target.value })} placeholder="Frage" />
                {['auswahl', 'mehrfach'].includes(q.typ) && <Textarea aria-label={`Antwortmöglichkeiten ${i + 1}`} rows={3} value={q.optionenText || ''} onChange={(e) => frage(i, { optionenText: e.target.value })} placeholder="Eine Möglichkeit je Zeile (2–12)" />}
                {['skala', 'bewertung'].includes(q.typ) && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input aria-label={`Beschriftung links bei Feld ${i + 1}`} maxLength={30} value={q.links || ''} onChange={(e) => frage(i, { links: e.target.value })} placeholder="1 = …" />
                    <Input aria-label={`Beschriftung rechts bei Feld ${i + 1}`} maxLength={30} value={q.rechts || ''} onChange={(e) => frage(i, { rechts: e.target.value })} placeholder={q.typ === 'skala' ? '10 = …' : '5 = …'} />
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {q.typ === 'zahl' && <Input aria-label={`Einheit bei Feld ${i + 1}`} className="w-40" maxLength={20} value={q.einheit || ''} onChange={(e) => frage(i, { einheit: e.target.value })} placeholder="Einheit, z. B. Liter" />}
                  {q.typ === 'bewertung' && <NativeSelect aria-label={`Darstellung bei Feld ${i + 1}`} className="w-36" value={q.stil || 'smiley'} onChange={(v) => frage(i, { stil: v })} options={[{ value: 'smiley', label: 'Smileys' }, { value: 'sterne', label: 'Sterne' }]} />}
                  {mitBewertung(q.typ) && <NativeSelect aria-label={`Richtung „besser“ bei Feld ${i + 1}`} className="w-48" value={String(q.better === -1 ? -1 : 1)} onChange={(v) => frage(i, { better: Number(v) })} options={BETTER} />}
                </div>
                {q.typ === 'ernaehrungseinschraenkungen' && <p className="text-xs text-muted-foreground">Die Auswahl kommt aus den Ausschlüssen des Kundenprofils; die Antwort schreibt sie dort hinein.</p>}
                {q.typ === 'aktivitaetslevel' && <p className="text-xs text-muted-foreground">kaum · wenig · mittel · hoch — die Antwort setzt Beruf und Freizeit im Kundenprofil.</p>}
                {q.typ === 'umfaenge' && <p className="text-xs text-muted-foreground">Taille, Hüfte, Oberschenkel, Brust in cm — landet in den Körperdaten.</p>}
                {['foto', 'fortschrittsfotos'].includes(q.typ) && <p className="text-xs text-muted-foreground">Nur mit Schalter und Einwilligung „Fortschrittsfotos“ — sonst fehlt das Feld in der App.</p>}
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap items-center gap-1.5">
            <Button type="button" size="sm" variant="outline" disabled={f.fragen.length >= 30} onClick={() => setF({ ...f, fragen: [...f.fragen, { typ: 'skala', text: '', pflicht: true, better: 1 }] })}><Plus className="w-4 h-4 mr-1" />Feld</Button>
            <span className="text-xs text-muted-foreground ml-1">Smart-Felder:</span>
            {Object.entries(SMART).filter(([t]) => typen.includes(t)).map(([t, s]) => (
              <Button key={t} type="button" size="sm" variant="ghost" disabled={smartDa(t) || f.fragen.length >= 30} onClick={() => smartDazu(t)}>{smartDa(t) && <Check className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}{s.label}</Button>
            ))}
          </div>
          <div className="rounded-md border p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Eigene Metriken <span className="font-normal text-muted-foreground">— freiwillige Felder hinter den Fragen, mit Verlauf in der Entwicklung</span></p>
              <Button type="button" size="sm" variant="outline" disabled={f.metriken.length >= 10} onClick={() => setF({ ...f, metriken: [...f.metriken, { name: '', typ: 'zahl', einheit: '', better: 1 }] })}><Plus className="w-4 h-4 mr-1" />Metrik</Button>
            </div>
            {f.metriken.map((m, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_8rem_7rem_11rem_auto] gap-2 items-center">
                <Input aria-label={`Name Metrik ${i + 1}`} required minLength={2} maxLength={60} value={m.name} onChange={(e) => metrik(i, { name: e.target.value })} placeholder="z. B. Schritte je Tag" />
                <NativeSelect aria-label={`Typ Metrik ${i + 1}`} value={m.typ} onChange={(v) => metrik(i, { typ: v })} options={Object.entries(METRIK_TYP).map(([value, label]) => ({ value, label }))} />
                <Input aria-label={`Einheit Metrik ${i + 1}`} maxLength={20} value={m.einheit || ''} onChange={(e) => metrik(i, { einheit: e.target.value })} placeholder="Einheit" disabled={!['zahl'].includes(m.typ)} />
                <NativeSelect aria-label={`Richtung Metrik ${i + 1}`} value={String(m.better === -1 ? -1 : 1)} onChange={(v) => metrik(i, { better: Number(v) })} options={BETTER} disabled={!['zahl', 'bewertung'].includes(m.typ)} />
                <Button type="button" size="icon" variant="ghost" aria-label={`Metrik ${i + 1} entfernen`} onClick={() => setF({ ...f, metriken: f.metriken.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
          <DialogFooter><Button type="submit" disabled={laeuft}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Zuweisen({ f, onClose }) {
  const [wer, setWer] = useState([]);
  const [faellig, setFaellig] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { setWer([]); setFaellig(''); }, [f]);
  const senden = async () => {
    setLaeuft(true);
    try { const { data } = await ernaehrungAPI.formularZuweisen(f.id, wer.map((m) => m.id), faellig); toast.success(`${data.zugewiesen}× zugewiesen — die App meldet sich per Push`); onClose(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  return (
    <Dialog open={!!f} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{f ? `„${f.titel}“ zuweisen` : ''}</DialogTitle>
          <DialogDescription>Je Mitglied gelten seine ausgeblendeten Felder (Karte „Check-ins & Formulare“ am Mitglied).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <MitgliedSuche onWahl={(m) => setWer((x) => (x.some((y) => y.id === m.id) ? x : [...x, m]))} />
          <div className="flex flex-wrap gap-1">
            {wer.map((m) => <Badge key={m.id} variant="secondary" className="font-normal gap-1">{m.name}<button type="button" aria-label={`${m.name} entfernen`} onClick={() => setWer(wer.filter((x) => x.id !== m.id))}>×</button></Badge>)}
            {!wer.length && <span className="text-xs text-muted-foreground">Mitglieder suchen und anklicken — mehrere möglich.</span>}
          </div>
          <FormField label="Fällig am (leer = heute)"><Input type="date" className="w-44" value={faellig} onChange={(e) => setFaellig(e.target.value)} /></FormField>
        </div>
        <DialogFooter><Button disabled={!wer.length || laeuft} onClick={senden}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Zuweisen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckinAutomatik({ einstellung, formulare, onNeu }) {
  const [e, setE] = useState(einstellung);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { setE(einstellung); }, [einstellung]);
  const speichern = async (neu) => {
    setLaeuft(true);
    try { await ernaehrungAPI.checkinEinstellung(neu); setE(neu); toast.success(neu.aktiv ? `Check-in läuft: jeden ${WOCHENTAG[neu.wochentag]} um ${neu.uhrzeit || '08:00'} Uhr` : 'Wöchentlicher Check-in aus'); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  const name = formulare.find((f) => f.id === e.formular_id)?.titel || '–';
  return (
    <Card>
      <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
        <span className="rounded-lg bg-primary/10 text-primary p-3 self-start"><ClipboardCheck className="w-6 h-6" aria-hidden="true" /></span>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">Wöchentlicher Check-in</h3>
            <Badge variant="outline" className={e.aktiv ? STATUS.beantwortet.cls : ''}>{e.aktiv ? 'aktiv' : 'aus'}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">Jedes Mitglied mit laufendem Ernährungscoaching bekommt einmal pro Woche „{name}“ per Push in die App — Standard-Zeitplan; je Mitglied lassen sich Formular, Zeitplan und Felder ändern. Gewicht, Umfänge und Metriken daraus landen in den Körperdaten.</p>
          <div className="flex flex-wrap items-end gap-2">
            <FormField label="Formular"><NativeSelect className="w-56" value={e.formular_id} onChange={(v) => setE({ ...e, formular_id: v })} options={formulare.map((f) => ({ value: f.id, label: f.titel }))} /></FormField>
            <FormField label="Wochentag"><NativeSelect className="w-36" value={String(e.wochentag)} onChange={(v) => setE({ ...e, wochentag: Number(v) })} options={WOCHENTAG.map((w, i) => ({ value: String(i), label: w }))} /></FormField>
            <FormField label="Uhrzeit"><Input type="time" className="w-28" value={e.uhrzeit || '08:00'} onChange={(ev) => setE({ ...e, uhrzeit: ev.target.value })} /></FormField>
            <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={e.erinnerung !== false} onCheckedChange={(v) => setE({ ...e, erinnerung: v })} aria-label="Erinnerung am Tag danach" />Erinnerung am Tag danach</label>
            <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={e.aktiv} onCheckedChange={(v) => setE({ ...e, aktiv: v })} aria-label="Wöchentlicher Check-in an" />{e.aktiv ? 'an' : 'aus'}</label>
            <Button disabled={laeuft} onClick={() => speichern(e)}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Übernehmen</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Eingang({ eingang }) {
  if (!eingang.length) return null;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Inbox className="w-4 h-4" aria-hidden="true" />Neue Antworten ({eingang.length})</CardTitle></CardHeader>
      <CardContent>
        <ul className="divide-y">
          {eingang.map((z) => (
            <li key={z.id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="min-w-0"><Link to={`/ernaehrung/${z.lead_id}#formulare`} className="font-medium hover:underline">{z.lead_name || 'Mitglied'}</Link> · {z.titel}{!z.gelesen && <Badge className="ml-2" variant="outline">neu</Badge>}</span>
              <span className="text-xs text-muted-foreground">{datum(z.beantwortet_am, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function FormulareReiter({ onUngelesen }) {
  const [daten, setDaten] = useState(null);
  const [eingang, setEingang] = useState([]);
  const [art, setArt] = useState('studio');
  const [kat, setKat] = useState('');
  const [vorschau, setVorschau] = useState(null);
  const [editor, setEditor] = useState(null);
  const [zuweisen, setZuweisen] = useState(null);
  const laden = useCallback(async () => {
    try {
      const [f, e] = await Promise.all([ernaehrungAPI.formulare(), ernaehrungAPI.formulareEingang()]);
      setDaten(f.data); setEingang(e.data.eingang); onUngelesen?.(e.data.ungelesen);
    } catch (err) { toast.error(fehlertext(err, 'Formulare konnten nicht geladen werden')); setDaten({ formulare: [], kategorien: {}, einstellung: { aktiv: false, formular_id: 'mf_checkin', wochentag: 6, uhrzeit: '08:00', erinnerung: true } }); }
  }, [onUngelesen]);
  useEffect(() => { laden(); }, [laden]);
  const liste = useMemo(() => (daten?.formulare || []).filter((f) => f.art === art && (!kat || f.kategorie === kat)), [daten, art, kat]);
  const loeschen = async (f) => {
    if (!window.confirm(`Formular „${f.titel}“ löschen? Bereits verschickte bleiben beim Mitglied.`)) return;
    try { await ernaehrungAPI.formularLoeschen(f.id); toast.success('Gelöscht'); laden(); } catch (err) { toast.error(fehlertext(err)); }
  };
  if (!daten) return <div className="space-y-2"><Skeleton className="h-32 w-full" /><Skeleton className="h-64 w-full" /></div>;
  return (
    <div className="space-y-4">
      <CheckinAutomatik einstellung={daten.einstellung} formulare={daten.formulare} onNeu={laden} />
      {/* Schalter des Ernährungsbereichs an einer Stelle: Körperanalyse (InBody), nur Leitung */}
      <KoerperanalyseSchalter />
      <Eingang eingang={eingang} />
      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 pt-3">
            <div role="tablist" className="flex gap-4">
              {[['studio', 'Studio-Formulare'], ['eigen', 'Eigene Formulare']].map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={art === k} onClick={() => setArt(k)}
                  className={`pb-2 text-sm font-medium border-b-2 -mb-px ${art === k ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{l}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 pb-2">
              <NativeSelect aria-label="Kategorie" className="w-44" value={kat} onChange={setKat} options={[{ value: '', label: 'alle Kategorien' }, ...Object.entries(daten.kategorien).map(([value, label]) => ({ value, label }))]} />
              <Button size="sm" onClick={() => setEditor({ art: 'neu' })}><Plus className="w-4 h-4 mr-1" />Neues Formular</Button>
            </div>
          </div>
          {liste.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">{art === 'eigen' ? 'Noch keine eigenen Formulare — „Anpassen“ an einem Studio-Formular ist der schnellste Start.' : 'Keine Formulare in dieser Kategorie.'}</p> : (
            <ul className="divide-y">
              {liste.map((f) => (
                <li key={f.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{f.titel}</p>
                    <p className="text-xs text-muted-foreground">{f.minuten} Min. · {f.fragen.length} Felder{f.metriken?.length ? ` + ${f.metriken.length} Metriken` : ''}{f.beschreibung ? ` · ${f.beschreibung}` : ''}</p>
                  </div>
                  <Badge variant="outline" className="self-start sm:self-auto font-normal">{ARTEN[f.formular_art] || ARTEN.frei}</Badge>
                  <Badge variant="outline" className="self-start sm:self-auto font-normal">{daten.kategorien[f.kategorie]}</Badge>
                  <div className="flex flex-wrap gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setVorschau(f)}><Eye className="w-4 h-4 mr-1" />Ansehen</Button>
                    {f.art === 'studio' ? <Button size="sm" variant="ghost" onClick={() => setEditor(f)}><Copy className="w-4 h-4 mr-1" />Anpassen</Button>
                      : <><Button size="sm" variant="ghost" onClick={() => setEditor(f)}><Pencil className="w-4 h-4 mr-1" />Bearbeiten</Button>
                        <Button size="icon" variant="ghost" aria-label={`${f.titel} löschen`} onClick={() => loeschen(f)}><Trash2 className="w-4 h-4" /></Button></>}
                    <Button size="sm" variant="outline" onClick={() => setZuweisen(f)}><UserPlus className="w-4 h-4 mr-1" />Zuweisen</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Vorschau f={vorschau} onClose={() => setVorschau(null)} />
      <Editor start={editor} daten={daten} onClose={() => setEditor(null)} onGespeichert={() => { setArt('eigen'); laden(); }} />
      <Zuweisen f={zuweisen} onClose={() => setZuweisen(null)} />
    </div>
  );
}

/* E5: Einstellungen je Kunde — eigenes Check-in-Formular, Zeitplan, Erinnerung, ausgeblendete Felder je Formular. */
function KundeEinstellungen({ leadId, kunde, formulare, onGespeichert }) {
  const [k, setK] = useState(null);
  const [feldForm, setFeldForm] = useState('mf_checkin');
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => {
    if (!kunde) return;
    setK({ formular_id: kunde.formular_id || '', eigenerZeitplan: !!kunde.zeitplan, wochentag: kunde.zeitplan?.wochentag ?? 6, uhrzeit: kunde.zeitplan?.uhrzeit || '08:00',
      erinnerung: kunde.erinnerung !== false, ausgeblendete_felder: { ...(kunde.ausgeblendete_felder || {}) } });
  }, [kunde]);
  if (!k) return null;
  const form = formulare.find((f) => f.id === feldForm);
  const felder = form ? [...form.fragen, ...(form.metriken || []).map((m) => ({ id: m.id, text: `${m.name} (Metrik)` }))] : [];
  const versteckt = new Set(k.ausgeblendete_felder[feldForm] || []);
  const umschalten = (id) => {
    const neu = new Set(versteckt); if (neu.has(id)) neu.delete(id); else neu.add(id);
    setK({ ...k, ausgeblendete_felder: { ...k.ausgeblendete_felder, [feldForm]: [...neu] } });
  };
  const speichern = async () => {
    setLaeuft(true);
    try {
      await ernaehrungAPI.formularKunde(leadId, {
        formular_id: k.formular_id || null, zeitplan: k.eigenerZeitplan ? { wochentag: Number(k.wochentag), uhrzeit: k.uhrzeit } : null,
        erinnerung: k.erinnerung, ausgeblendete_felder: k.ausgeblendete_felder,
      });
      toast.success('Einstellungen gespeichert — gelten ab dem nächsten Check-in'); onGespeichert?.();
    } catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  return (
    <div className="rounded-md border p-3 space-y-3">
      <p className="text-sm font-medium flex items-center gap-2"><Settings2 className="w-4 h-4" aria-hidden="true" />Für dieses Mitglied</p>
      <div className="flex flex-wrap items-end gap-2">
        <FormField label="Check-in-Formular"><NativeSelect className="w-52" value={k.formular_id} onChange={(v) => setK({ ...k, formular_id: v })} options={[{ value: '', label: 'Standard' }, ...formulare.map((f) => ({ value: f.id, label: f.titel }))]} /></FormField>
        <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={k.eigenerZeitplan} onCheckedChange={(v) => setK({ ...k, eigenerZeitplan: v })} aria-label="Eigener Zeitplan" />eigener Zeitplan</label>
        {k.eigenerZeitplan && (
          <>
            <FormField label="Wochentag"><NativeSelect className="w-36" value={String(k.wochentag)} onChange={(v) => setK({ ...k, wochentag: Number(v) })} options={WOCHENTAG.map((w, i) => ({ value: String(i), label: w }))} /></FormField>
            <FormField label="Uhrzeit"><Input type="time" className="w-28" value={k.uhrzeit} onChange={(e) => setK({ ...k, uhrzeit: e.target.value })} /></FormField>
          </>
        )}
        <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={k.erinnerung} onCheckedChange={(v) => setK({ ...k, erinnerung: v })} aria-label="Erinnerung bei fehlendem Check-in" />Erinnerung</label>
      </div>
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">Felder ausblenden in</span>
          <NativeSelect aria-label="Formular für ausgeblendete Felder" className="w-52" value={feldForm} onChange={setFeldForm} options={formulare.map((f) => ({ value: f.id, label: f.titel }))} />
        </div>
        <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {felder.map((q) => (
            <li key={q.id}><label className="flex items-center gap-2"><input type="checkbox" checked={!versteckt.has(q.id)} onChange={() => umschalten(q.id)} />{q.text}</label></li>
          ))}
        </ul>
        {Object.entries(k.ausgeblendete_felder).filter(([, ids]) => ids.length).map(([fid, ids]) => (
          <p key={fid} className="text-xs text-muted-foreground">{formulare.find((f) => f.id === fid)?.titel || fid}: {ids.length} Feld(er) ausgeblendet</p>
        ))}
      </div>
      <Button size="sm" disabled={laeuft} onClick={speichern}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button>
    </div>
  );
}

/* Am Mitglied: Check-ins und Formulare mit Antworten, Feedback schreiben, Formular zuweisen, Einstellungen je Kunde. */
export function MitgliedFormulare({ leadId, onNeu }) {
  const [daten, setDaten] = useState(null);
  const [formulare, setFormulare] = useState([]);
  const [wahl, setWahl] = useState('mf_anamnese');
  const [offen, setOffen] = useState(null);
  const [einstellungen, setEinstellungen] = useState(false);
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const laden = useCallback(async () => {
    try { setDaten((await ernaehrungAPI.mitgliedFormulare(leadId)).data); } catch { setDaten({ zuweisungen: [], ungelesen: 0, kunde: null }); }
  }, [leadId]);
  useEffect(() => { laden(); ernaehrungAPI.formulare().then((r) => setFormulare(r.data.formulare)).catch(() => {}); }, [laden]);
  const zs = daten?.zuweisungen ?? null;
  const aufklappen = (z) => {
    setOffen(offen === z.id ? null : z.id); setText('');
    if (z.status === 'beantwortet' && !z.gelesen) ernaehrungAPI.zuweisungGelesen(z.id).then(laden).catch(() => {});
  };
  const gelesen = async (z, wert) => {
    try { await ernaehrungAPI.zuweisungGelesen(z.id, wert); laden(); onNeu?.(); } catch (err) { toast.error(fehlertext(err)); }
  };
  const antworten = async (z) => {
    setLaeuft(true);
    try { await ernaehrungAPI.coachAntwort(z.id, text); toast.success('Feedback gesendet — das Mitglied bekommt einen Push'); setText(''); laden(); onNeu?.(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  const zuweisen = async () => {
    try { await ernaehrungAPI.formularZuweisen(wahl, [leadId]); toast.success('Zugewiesen — die App meldet sich per Push'); laden(); }
    catch (err) { toast.error(fehlertext(err)); }
  };
  return (
    <Card id="formulare">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="w-4 h-4" aria-hidden="true" />Check-ins & Formulare
          {daten?.ungelesen > 0 && <Badge variant="outline" className={STATUS.offen.cls}>{daten.ungelesen} ungelesen</Badge>}
          <Button size="sm" variant="ghost" className="ml-auto" aria-expanded={einstellungen} onClick={() => setEinstellungen(!einstellungen)}><Settings2 className="w-4 h-4 mr-1" />Einstellungen</Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {einstellungen && daten?.kunde && <KundeEinstellungen leadId={leadId} kunde={daten.kunde} formulare={formulare} onGespeichert={laden} />}
        {zs === null ? <Skeleton className="h-16 w-full" /> : zs.length === 0 ? <p className="text-sm text-muted-foreground">Noch nichts verschickt.</p> : (
          <ul className="space-y-1.5">
            {zs.map((z) => (
              <li key={z.id} className="rounded-md border">
                <button type="button" aria-expanded={offen === z.id} onClick={() => aufklappen(z)} className="w-full text-left px-3 py-2 flex flex-wrap items-center gap-2 text-sm hover:bg-accent/40 rounded-md">
                  <span className="font-medium flex-1 min-w-0">{z.titel}</span>
                  <Badge variant="outline" className={(STATUS[z.status] || STATUS.offen).cls}>{(STATUS[z.status] || STATUS.offen).label}{z.status === 'entwurf' && z.ueberfaellig ? ' · überfällig' : ''}</Badge>
                  {z.status === 'beantwortet' && !z.gelesen && <Badge variant="outline">neu</Badge>}
                  {z.coach_antwort && <Badge variant="secondary" className="font-normal">Feedback</Badge>}
                  <span className="text-xs text-muted-foreground w-full">{z.status === 'beantwortet' ? `eingereicht ${datum(z.beantwortet_am, lang)}` : `fällig ${datum(z.faellig_am, lang)}`}{z.quelle === 'woechentlich' ? ' · automatisch' : ''}{z.erinnert_am ? ' · erinnert' : ''}{z.ausgeblendet?.length ? ` · ${z.ausgeblendet.length} Feld(er) ausgeblendet` : ''}</span>
                </button>
                {offen === z.id && (
                  <div className="border-t px-3 py-3 space-y-3">
                    {z.status === 'beantwortet' ? <Antworten z={z} /> : z.status === 'entwurf' ? <><p className="text-xs text-muted-foreground">Entwurf des Mitglieds — noch nicht eingereicht.</p><Antworten z={z} /></> : <p className="text-sm text-muted-foreground">Noch nicht beantwortet.</p>}
                    {z.status === 'beantwortet' && (
                      <label className="flex items-center gap-2 text-sm"><Switch checked={!!z.gelesen} onCheckedChange={(v) => gelesen(z, v)} aria-label="Als gelesen markieren" />{z.gelesen ? 'gelesen' : 'Als gelesen markieren'}</label>
                    )}
                    {z.coach_antwort ? (
                      <div className="rounded-md bg-muted p-2 text-sm"><p className="text-xs text-muted-foreground mb-0.5">Feedback von {z.coach_name} · {datum(z.coach_antwort_am, lang)}</p><p className="whitespace-pre-line">{z.coach_antwort}</p></div>
                    ) : z.status === 'beantwortet' && (
                      <div className="space-y-2">
                        <Textarea aria-label="Feedback an das Mitglied" rows={3} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Kurzes Feedback — kommt per Push in die App" />
                        <Button size="sm" disabled={laeuft || text.trim().length < 2} onClick={() => antworten(z)}>{laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Feedback senden</Button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-2 pt-1">
          <FormField label="Formular schicken"><NativeSelect className="w-52 max-w-full" value={wahl} onChange={setWahl} options={formulare.map((f) => ({ value: f.id, label: f.titel }))} /></FormField>
          <Button size="sm" variant="outline" onClick={zuweisen}><UserPlus className="w-4 h-4 mr-1" />Zuweisen</Button>
        </div>
      </CardContent>
    </Card>
  );
}
